import {
  channelToChannelName,
  RealtimeChannel,
  SubscriptionChannel,
  SubscriptionChannelEvent,
} from '@fluentsms/lib-subscriptions';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ZodSchema } from 'zod';
import { SubscriptionChannelEventSchema, SubscriptionOptions } from '../types';
import { isSubset } from '../utils';
import { ChannelsContext } from './ChannelsProvider';

type SubscriptionEventCallback<T = unknown> = (data: SubscriptionChannelEvent<T>) => void;

export type EventContextType = {
  subscribe: <T extends SubscriptionChannelEventSchema>(
    options: SubscriptionOptions<T>,
    callback: SubscriptionEventCallback<T>,
  ) => { unsubscribe: () => void };
};

export function createEventContext(): React.Context<EventContextType> {
  return createContext<EventContextType>({
    subscribe: () => {
      console.error('Event context not set');
      return { unsubscribe: () => undefined };
    },
  });
}

/**
 * Provider for a ably channel event context. Establishes a subscription with ably to receive events within the context
 * Any child components can react to events within the context
 */
export const EventContextProvider: React.FC<{
  children: React.ReactNode;
  channel: SubscriptionChannel | null;
  context: React.Context<EventContextType>;
}> = ({ children, channel, context }) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const callbacks = useRef(new Array<[SubscriptionOptions<ZodSchema>, SubscriptionEventCallback<any>]>());
  const { subscribe: channelSubscribe, unsubscribe: channelUnsubscribe, debugEnabled } = useContext(ChannelsContext);
  const channelName = channel ? channelToChannelName(channel) : '';
  const [ablyChannel, setAblyChannel] = useState<RealtimeChannel | undefined>(undefined);

  useEffect(() => {
    if (channelName && channelSubscribe) {
      const ablyChannel = channelSubscribe(channelName);
      setAblyChannel(ablyChannel);

      if (ablyChannel) {
        callbacks.current = [];
        ablyChannel.subscribe((message) => {
          const eventData = JSON.parse(message.data);

          if (debugEnabled) {
            console.log(`Received event on channel ${ablyChannel.name}: `, JSON.stringify(eventData));
          }

          callbacks.current.forEach(([options, cb]) => {
            const parsed = options.schema.safeParse(eventData);
            if (parsed.success && isSubset({ ...options.filter }, eventData)) {
              debugLog(debugEnabled, 'Executing', ablyChannel?.name, options);
              if (options.isOneTimeSubscription) {
                debugLog(debugEnabled, 'Unregistering', ablyChannel?.name, options);

                callbacks.current = callbacks.current.filter(([_, callback]) => callback !== cb);
              }
              cb(parsed.data);
            }
          });
        });
      }
      return () => {
        channelUnsubscribe(channelName);
      };
    }
  }, [channel?.context, channelName, channelSubscribe, channelUnsubscribe, debugEnabled]);

  const subscribe = useCallback(
    <T extends SubscriptionChannelEventSchema>(
      options: SubscriptionOptions<T>,
      callback: SubscriptionEventCallback<T>,
    ) => {
      if (ablyChannel?.state !== 'attached' && !options.continueWhenNotConnected) {
        throw new Error(
          `Channel not subscribed: ${ablyChannel?.name}. state: ${ablyChannel?.state}, errorReason: ${JSON.stringify(
            ablyChannel?.errorReason,
          )}`,
        );
      }

      debugLog(debugEnabled, 'Registering', ablyChannel?.name, options);

      callbacks.current.push([options, callback]);
      return {
        unsubscribe: () => {
          debugLog(debugEnabled, 'Unregistering', ablyChannel?.name, options);
          callbacks.current = callbacks.current.filter(([_, cb]) => cb !== callback);
        },
      };
    },
    [ablyChannel?.name, ablyChannel?.state, debugEnabled],
  );

  return <context.Provider value={{ subscribe }}>{children}</context.Provider>;
};

function debugLog(
  debugEnabled: boolean,
  action: 'Registering' | 'Unregistering' | 'Executing',
  name: string | undefined,
  options: SubscriptionOptions<ZodSchema>,
) {
  if (!debugEnabled) return;

  if (!options.schema.description) {
    console.log(
      "Schema doesn't have a description. Please add a description to the schema to make debugging easier: ",
      JSON.stringify(options.schema),
    );
  }

  console.log(`${action} callback on channel ${name ?? 'NO CHANNEL'} for schema ${options.schema.description}`);
}
